import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell, GoogleButton } from "./Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Register() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "", username: "", display_name: "" });
  const [busy, setBusy] = useState(false);
  const [uState, setUState] = useState(null);

  useEffect(() => {
    const u = form.username.trim();
    if (u.length < 3) { setUState(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/username-available", { params: { username: u } });
        setUState(data);
      } catch { setUState(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [form.username]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (uState && !uState.available) { toast.error("Please choose an available username."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data.user);
      toast.success("Account created! Let's set you up.");
      nav("/onboarding", { replace: true });
    } catch (err) { toast.error(apiErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <AuthShell title="Join SINGVI KARAOKE" subtitle="Create your free account · 2 free recordings">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-xs">Display name</Label>
          <Input required value={form.display_name} onChange={set("display_name")} data-testid="reg-name"
            className="mt-1.5 bg-input border-white/10" placeholder="Your name" />
        </div>
        <div>
          <Label className="text-xs">Username</Label>
          <div className="relative">
            <Input required value={form.username} onChange={set("username")} data-testid="reg-username"
              className="mt-1.5 bg-input border-white/10 pr-9" placeholder="singer123" />
            {uState && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2">
                {uState.available ? <Check className="w-4 h-4 text-green-400" /> : <X className="w-4 h-4 text-neon-pink" />}
              </span>
            )}
          </div>
          {uState && <p className={`text-xs mt-1 ${uState.available ? "text-green-400" : "text-neon-pink"}`} data-testid="username-status">{uState.reason}</p>}
        </div>
        <div>
          <Label className="text-xs">Email</Label>
          <Input type="email" required value={form.email} onChange={set("email")} data-testid="reg-email"
            className="mt-1.5 bg-input border-white/10" placeholder="you@example.com" />
        </div>
        <div>
          <Label className="text-xs">Password</Label>
          <Input type="password" required value={form.password} onChange={set("password")} data-testid="reg-password"
            className="mt-1.5 bg-input border-white/10" placeholder="Min 8 characters" />
        </div>
        <Button type="submit" disabled={busy} data-testid="reg-submit"
          className="w-full rounded-full bg-neon-pink hover:bg-neon-pink/90 glow-pink font-bold py-6">
          {busy ? "Creating..." : "Create free account"}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-white/10" /><span className="text-xs text-muted-foreground">or</span><div className="flex-1 h-px bg-white/10" /></div>
      <GoogleButton />
      <p className="text-center text-sm text-muted-foreground mt-6">
        Already have an account? <Link to="/login" className="text-neon-cyan font-semibold" data-testid="to-login">Log in</Link>
      </p>
    </AuthShell>
  );
}
